import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import { browserName, browserVersion, isIE, isSafari } from 'react-device-detect';
import styled from 'styled-components';
import LogoIcon from '../../../img/sprite/my-lw-logo.svg';
import MobileNavIcon from '../../../img/sprite/mobile-nav-icon.svg';
import HeaderLinks from './HeaderLinks';
import BreadCrumb from './BreadCrumb';
import ArrowIcon from '../../../img/sprite/arrow-right.svg';
import AlertIcon from '../../../img/sprite/alert-circle.svg';
import MobileNavMenu from './MobileNavMenu';
import NewFeaturesToolTip from './NewFeaturesToolTip';
import { useViewport } from '../hooks/viewport';
import { ENABLE_ORG_MANAGEMENT } from '../constants';

import css from '../../../css/css-variables.json';

const StyledLink = styled(Link)`
  width: 12.875em;
  margin-left: 1.875em;
  margin-top: 0.438em;

  @media(max-width: 720px) {
    width: 6.375em;
    margin-left: 0.438em;
    margin-top: 0.563em;
    display: inline-block;
  }
`;

const Header = styled.header`
  border-bottom-style: solid;
  border-bottom-color: ${css.colors.AccentGray};
  border-bottom-width: 2px;
  flex: 0 ${css.header.Height};
  height: ${css.header.Height};
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 5;
  background-color: ${css.colors.AccentLight};

  @media(max-width: 720px) {
    flex: none;
    border-bottom-width: 0px;
    height: 2.813em;
    padding-bottom: 0.438em;
    z-index: 0;
  }
`;

const Svg = styled.svg`
  height: 4.688em;
  width: 12.875em;
  background-color: transparent;

  @media(max-width: 720px) {
    width: 100%;
    height: 1.750em;
  }
`;

const SvgNav = styled.svg`
  width: 2.188em;
  height: 1.875em;
  ${props => props.isOpen ? 'padding: 0.250em;' : ''}
`;

const StyledLinkNav = styled(Link)`
  display: flex;
  align-self: center;
  margin-right: 0.625em;
  margin-top: 0.375em;
  color: #616161;
  position: relative;
`;

const SpanNav = styled.span`
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgba(255,0,0,0);
  width: 2.188em;
  height: 1.875em;
`;

const WrapperBreadCrumb = styled.div`
  position: relative;
`;

const SvgBreadCrumb = styled.svg`
  transform: rotate(-90deg);
  color: #dcdcdc;
  position: absolute;
  top: -0.625em;
  right: 1.250em;
  z-index: 1;
`;

const AlertSvg = styled.svg`
  position: relative;
  bottom: 0.1em;
  right: 0.2em;
`;

const Banner = styled.div`
  padding: 0.5em;
  background-color: #f3dddf;
  color: #b54a42;
  text-align: center;
`;

const UnsupportedBrowserMessage = () => {
  return (
    <Banner>
      <AlertSvg><use xlinkHref={`#${AlertIcon.id}`} /></AlertSvg>
      <strong>{browserName} {browserVersion}</strong> is not a supported browser. Please use a <a href="https://browsehappy.com" target="_blank" without rel="noreferrer">recommended browser</a> to improve your overall experience and security.
    </Banner>
  );
};

class _HeaderComponent extends React.Component {

  navIconOrLinks() {
    const { breakPoints, isLoggedIn, location, navToggle, customer, onLogOut, isMobile } = this.props;

    return (
      isMobile
        ? // span is a fix for IE10 issue similar to https://github.com/facebook/react/issues/4963
        <StyledLinkNav onClick={navToggle} to={`${location.pathname}#`} data-qa-hook="mobileHamburgerNav.icon">
          <SvgNav>
            <use xlinkHref={`#${MobileNavIcon.id}`} />
          </SvgNav>
          <SpanNav />
        </StyledLinkNav>
        :
        <HeaderLinks
          isLoggedIn={isLoggedIn}
          onLogOut={onLogOut}
          path={location.pathname}
          customer={customer}
        />
    );
  }

  renderMobileNav() {
    const { location, isOpen, breakPoints, isLoggedIn, onLogOut, navToggle, referredBy, customer, isMobile } = this.props;

    return (isOpen && isMobile) ?
      <MobileNavMenu
        location={location}
        breakPoints={breakPoints}
        isLoggedIn={isLoggedIn}
        onLogOut={onLogOut}
        isOpen={isOpen}
        navToggle={navToggle}
        referredBy={referredBy}
        customer={customer}
      />
      : null;
  }

  renderBreadCrumb() {
    const { isOpen, location, isMobile } = this.props;

    const breadCrumb = (!isOpen && isMobile) ?
      (
        <WrapperBreadCrumb>
          <SvgBreadCrumb><use xlinkHref={`#${ArrowIcon.id}`} /></SvgBreadCrumb>
          <BreadCrumb location={location} />
        </WrapperBreadCrumb>
      )
      : null;

    return breadCrumb;
  }

  render() {
    const isUnsupportedBrowser = isIE || (isSafari && browserVersion < 11);

    return (
      <div>
        {isUnsupportedBrowser && <UnsupportedBrowserMessage />}
        <Header>
          <StyledLink to={'/'} data-qa-hook="myLifeWay.logo">
            <Svg>
              <use xlinkHref={`#${LogoIcon.id}`} />
            </Svg>
          </StyledLink>
          {this.props.isMobile && ENABLE_ORG_MANAGEMENT && <NewFeaturesToolTip />}
          {this.navIconOrLinks()}
        </Header>
        {this.renderMobileNav()}
        {this.renderBreadCrumb()}
      </div>
    );
  }
}

const HeaderComponent = props => {
  const { isMobile } = useViewport();
  return <_HeaderComponent {...props} isMobile={isMobile}>{props.children}</_HeaderComponent>;
};

const { object, bool, func, string } = PropTypes;

HeaderComponent.propTypes = {
  breakPoints: object.isRequired,
  isLoggedIn: bool.isRequired,
  location: object.isRequired,
  isOpen: bool.isRequired,
  navToggle: func.isRequired,
  referredBy: string,
  customer: object,
  onLogOut: func
};

export default HeaderComponent;
